import type { DigitalAssetDetailSignature } from 'tio-common/services/digital-assets';

export const SLR_LOAN_STANDING_FEATURE_FLAG = 'slrLoanStandingVerification';

export type SlrLoanStandingStatus =
  | 'unknown'
  | 'pending_upload'
  | 'processing'
  | 'eligible'
  | 'ineligible'
  | 'try_again'
  | 'cannot_determine';

export type SlrLoanStandingAction =
  'upload_document' | 'upload_newer_document' | 'confirm_name_match' | 'contact_support' | 'none';

/**
 * Raw good-standing state emitted by tio-api's EmployeePlanAccount
 * (`good_standing` column). The richer presentation status above
 * (`SlrLoanStandingStatus`) is derived from this plus the reason via
 * `deriveSlrLoanStandingStatus()`.
 */
export type SlrGoodStandingState = 'unknown' | 'eligible' | 'ineligible' | 'undeterminable';

/**
 * Raw good-standing reason emitted by tio-api's EmployeePlanAccount
 * (`good_standing_reason` column), plus the document-pipeline second-document
 * asks that surface through the same field (see tio-front-end#8714 / D4).
 */
export type SlrGoodStandingReason =
  | 'none'
  | 'missing_document'
  | 'stale_document'
  | 'outside_audit_window'
  | 'missing_required_observations'
  | 'name_mismatch'
  | 'status_not_in_good_standing'
  | 'delinquent_or_collections'
  | 'defaulted'
  | 'parent_plus_only'
  | 'undeterminable'
  | 'manual_override'
  | 'nslds_required_for_federal_non_repayment_status'
  | 'statement_required_for_repayment_status';

export interface SlrLoanStandingFailure {
  code: string;
  field?: string;
  message?: string;
}

export interface SlrLoanStandingAuditState {
  employeePlanAccountId?: string | null;
  status: SlrLoanStandingStatus;
  primaryReason?: string | null;
  reasons?: string[];
  failures?: SlrLoanStandingFailure[];
  action: SlrLoanStandingAction;
  sourceDocumentId?: string | null;
  featureEnabled: boolean;
}

export interface SlrLoanStandingCablePayload {
  employeePlanAccountId?: string | null;
  assetUuid?: string | null;
  sourceDocumentId?: string | null;
  status?: SlrLoanStandingStatus;
  primaryReason?: string | null;
  reasons?: string[];
  failures?: SlrLoanStandingFailure[];
  action?: SlrLoanStandingAction;
}

export interface SlrLoanStandingDocumentUploadPayload {
  asset: DigitalAssetDetailSignature;
}

export interface SlrLoanStandingPresentation {
  titleKey: string;
  descriptionKey: string;
  ctaKey?: string;
  intent: 'info' | 'warning' | 'success';
  requiresAttention: boolean;
}
